export type CallTurn = { role: "caller" | "ai"; text: string };
export type Stat = { num: string; label: string };
export type Objection = { q: string; a: string };

export type VerticalData = {
  slug: string;
  route: string;
  vertical: string;
  title: string;
  metaDescription: string;
  hero: { h1: string; intro: string };
  directAnswer: string;
  /** Served Tampa paragraph (prerendered and mounted), from this page's own published facts. */
  tampa: string;
  pain: { headline: string; story: string; cost: string };
  sampleCall: { context: string; transcript: CallTurn[]; heading?: string };
  aiKnows: { vocabulary: string[]; integrations: string[]; callPatterns: string[]; integrationNote?: string; integrationGuide?: { href: string; label: string } };
  stats: Stat[];
  objections: Objection[];
  callFaqs: Objection[];
  whyChleb: string[];
  relatedComparisons: { competitor: string; slug: string; note: string }[];
};

// relatedComparisons notes below cite two recurring competitor prices:
// Smith.ai's 90-call plan at $810/month (https://smith.ai/pricing/receptionists)
// and Goodcall's current monthly per-agent plans at $79/$129/$299 for
// 100/500/2,000 unique customers, with tier-specific overage
// (https://www.goodcall.com/pricing). Goodcall rechecked on 2026-10-09;
// Smith.ai retains its 2026-09-11 review date. Keep this dated and re-verify before reusing these figures.
export const VERTICALS: VerticalData[] = [
  {
    slug: "hvac",
    route: "/hvac",
    vertical: "HVAC",
    title: "AI Receptionist for HVAC Companies | Chleb AI",
    metaDescription:
      "Chleb AI captures HVAC service requests, caller details and urgency after hours. Review calendar access, dispatch rules and handoffs before launch.",
    hero: {
      h1: "AI receptionist for HVAC companies. Capture after-hours service requests.",
      intro:
        "Capture the service address, no-cool or no-heat details, and requested timing while your technicians are working. Chleb configures the intake and team handoff around your service area and on-call rules. A request becomes a confirmed visit only after the approved scheduling workflow succeeds.",
    },
    directAnswer:
      "Capture the service address, no-cool or no-heat details, and requested timing while your technicians are working. Chleb configures the intake and team handoff around your service area and on-call rules. A request becomes a confirmed visit only after the approved scheduling workflow succeeds.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this HVAC call flow for contractors in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "What missed HVAC calls actually cost you",
      story:
        "HVAC shops lose inbound calls to voicemail every week, and the worst losses happen in the moments that matter most: 11pm in July, the AC just died, the homeowner has three contractor numbers and is calling them in order. Voicemail loses that job. Chleb AI answers that call, qualifies the emergency, and can request the visit on your calendar.",
      cost:
        "A call that reaches voicemail may send the caller to another shop. A configured follow-up can preserve the request and give your team a chance to respond.",
    },
    sampleCall: {
      context: "Illustrative fictional scenario: Homeowner calls at 10:42pm. AC out. 96 degrees outside.",
      transcript: [
  {
    "role": "caller",
    "text": "Our air conditioning stopped working. Can someone come out tonight?"
  },
  {
    "role": "ai",
    "text": "I can take an urgent service request for the on-call team. What is the property address?"
  },
  {
    "role": "caller",
    "text": "It is 1247 Hawthorne Drive. My name is Sarah."
  },
  {
    "role": "ai",
    "text": "Thank you, Sarah. Is the system not cooling at all, and when did the problem start?"
  },
  {
    "role": "caller",
    "text": "No cool air. It stopped about an hour ago."
  },
  {
    "role": "ai",
    "text": "I will include that in the request. The on-call team needs to confirm whether a technician can visit tonight; I cannot promise an arrival time."
  },
  {
    "role": "caller",
    "text": "Please ask them to call me."
  },
  {
    "role": "ai",
    "text": "What callback number should the team use?"
  }
],
    },
    aiKnows: {
      integrationNote: "These field-service systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, service-area rules, calendar or dispatch writes, duplicate handling, failed delivery, and unanswered on-call handoff before promising a connection.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      vocabulary: [
        "no-cool, no-heat, low refrigerant, frozen coil, blown capacitor",
        "split system, mini-split, package unit, heat pump, dual-fuel",
        "service area boundaries (you tell us, we won't quote outside them)",
        "dispatch fee vs diagnostic fee vs estimate fee",
        "seasonal pricing for peak cooling and heating windows",
        "maintenance plan upsell triggers (annual tune-up due, etc)",
      ],
      integrations: [
        "ServiceTitan (compatibility assessed during setup; verify the account)",
        "Housecall Pro (compatibility assessed during setup; verify the account)",
        "Jobber (compatibility assessed during setup; verify the account)",
        "FieldEdge (compatibility assessed during setup; verify the account)",
        "Generic CRM via webhook (subject to endpoint access and a successful test) for shops on custom stacks",
      ],
      callPatterns: [
        "Emergency dispatch (priority booking, gate codes and access notes for the on-call tech)",
        "Routine maintenance booking (next available, lead-time aware)",
        "Replacement quote requests (collect age of system, tonnage, fuel type, schedule estimator)",
        "Warranty and follow-up requests (collect reported install date for office review)",
        "Membership plan inquiries (qualify, book sales call)",
      ],
    },
    stats: [
      { num: "configured after-hours coverage", label: "after-hours and weekend calls can be answered instead of hitting voicemail" },
    ],
    objections: [
      {
        q: "Will it sound like a robot to my customers?",
        a: "Use a trial call with your business wording to judge the voice and responses. Test a human handoff separately, including what happens when nobody answers and what caller details reach the recipient.",
      },
      {
        q: "How do we keep requests within the technicians’ availability?",
        a: "We configure the calendar with your real service area, real on-call schedule, and real availability windows. Test unavailable slots and conflicts on the actual calendar before allowing appointment confirmation. If a caller asks for something out-of-bounds, the AI takes the message and routes it to a human queue.",
      },
      {
        q: "Do I have to switch CRMs?",
        a: "No. Chleb runs Go High Level under the hood, but the call summary and booking go to your team, and ServiceTitan, Housecall Pro, Jobber, and FieldEdge compatibility is assessed during setup; verify the account before promising a connection if that's where your office and techs already work.",
      },
      {
        q: "What about Spanish-speaking callers?",
        a: "We're US English first today. Bilingual handoff is on the roadmap. If Spanish is critical to your market, talk to us about timeline before signing.",
      },
    ],
    callFaqs: [
      {
        q: "What happens if a no-heat call involves an infant or elderly resident?",
        a: "The AI flags the call as urgent and can send a configured summary to your on-call contact, including any access notes the caller gives, so your team can dispatch the tech.",
      },
      {
        q: "Can it tell a routine tune-up call from a real no-cool emergency?",
        a: "Yes. It asks a triage question first, then can submit a routine maintenance request for the next available slot or flags a no-cool or no-heat emergency as urgent for your team.",
      },
      {
        q: "What does it do with a whole-system replacement quote call?",
        a: "It collects the system's age, tonnage, and fuel type, then can request the estimator on your connected booking calendar during the same call.",
      },
      {
        q: "Is this a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "What happens on a warranty or follow-up call about a past install?",
        a: "It can collect the caller’s reported installation date and reason for calling, then route the request to your office. Reading an installation record or confirming warranty eligibility requires a separately verified system connection.",
      },
      {
        q: "Does the AI quote the after-hours call-out fee on the phone?",
        a: "Your team chooses whether the receptionist may state an approved fee or must leave that question for staff. Test the exact wording and the fallback when a fee is unknown; the sample request does not establish a price.",
      },
    ],
    whyChleb: [
      "Configured voice, follow-up and CRM workflow in one bundle",
      "Vertical-tuned out of the box, not configured generically",
      "Keeps your existing business number",
      "Missed-call follow-up can be configured and tested",
      "One monthly subscription; usage is billed separately at carrier cost",
    ],
    relatedComparisons: [
      { competitor: "Smith.ai", slug: "smith-ai", note: "Compare the current Smith.ai offer with a configured HVAC call flow" },
      { competitor: "Goodcall", slug: "goodcall", note: "Compare the current Goodcall offer with a configured call flow" },
      { competitor: "Synthflow", slug: "synthflow", note: "Build-your-own voice agent if you have engineering capacity" },
    ],
  },
  {
    slug: "dental",
    route: "/dental",
    vertical: "Dental",
    title: "AI Receptionist for Dental Practices | Chleb AI",
    metaDescription:
      "Chleb AI captures dental appointment requests and routes insurance questions to your office. Review intake fields and calendar access before launch.",
    hero: {
      h1: "AI receptionist for dental practices. Give new-patient inquiries a clear next step.",
      intro:
        "Give new-patient and returning-patient callers a way to request an appointment after your front desk closes. Chleb configures the questions and staff handoff with your practice. Your team approves intake fields, verifies coverage and confirms scheduling; a named practice system is not a proven connection.",
    },
    directAnswer:
      "Give new-patient and returning-patient callers a way to request an appointment after your front desk closes. Chleb configures the questions and staff handoff with your practice. Your team approves intake fields, verifies coverage and confirms scheduling; a named practice system is not a proven connection.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this dental call flow for practices in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "Why dental practices leak new patients overnight",
      story:
        "A new patient is worth years of visits, not one appointment. They call at 6pm after their old dentist no-call no-showed them, get your voicemail, then call the practice down the street. Chleb AI answers the call, asks which insurance they have, can request a consultation, and can send request details after the appointment is verified.",
      cost:
        "A new patient who reaches voicemail after hours often calls the next practice instead of waiting for a callback. Answering that call live keeps the new-patient pipeline from leaking overnight.",
    },
    sampleCall: {
      context: "Illustrative fictional scenario: Caller at 6:15pm asking about a new-patient cleaning.",
      transcript: [
  {
    "role": "caller",
    "text": "I am looking for a new dentist. Do you take Delta Dental PPO?"
  },
  {
    "role": "ai",
    "text": "The front desk can check your plan. I can record the plan name with an appointment request. Are you looking for a cleaning or another type of visit?"
  },
  {
    "role": "caller",
    "text": "A cleaning. Thursday afternoon would be best."
  },
  {
    "role": "ai",
    "text": "I can note Thursday afternoon as your preference. The practice will confirm the appointment and any insurance details. What first name should I put on the request?"
  },
  {
    "role": "caller",
    "text": "Sarah."
  },
  {
    "role": "ai",
    "text": "Thank you, Sarah. What callback number should the front desk use? Please save member numbers and health details for the intake process the practice provides."
  }
],
    },
    aiKnows: {
      integrationNote: "These practice-management systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, approved intake fields, privacy requirements, booking writes, duplicate handling, and failed delivery during setup.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      vocabulary: [
        "PPO vs HMO vs fee-for-service, in-network vs out-of-network",
        "common procedures: cleaning, exam, x-rays, crown, root canal, extraction, whitening, Invisalign",
        "new-patient vs recall, hygiene-only vs comprehensive",
        "specialty referral patterns (oral surgery, endo, ortho)",
        "deductible vs annual max language",
      ],
      integrations: [
        "Dentrix (compatibility assessed during setup; verify the account)",
        "Open Dental (compatibility assessed during setup; verify the account)",
        "EagleSoft (compatibility assessed during setup; verify the account)",
        "Curve Dental, Denticon (compatibility assessed during setup; verify the account)",
        "PMS webhook path, subject to account access and a successful test",
      ],
      callPatterns: [
        "New patient inquiry (insurance intake + consultation booking)",
        "Existing patient recall (schedule next cleaning)",
        "Caller-reported urgent dental concern routed under practice-approved staff rules",
        "Insurance benefit verification (callback during business hours)",
        "Treatment plan questions (route to treatment coordinator)",
        "Cosmetic and Invisalign consultations (sales-qualified handoff)",
      ],
    },
    stats: [],
    objections: [
      {
        q: "Is the AI HIPAA-aware?",
        a: "Review approved intake fields, access, retention and required agreements with your practice before launch. Use the practice-approved intake process for sensitive details. Naming a practice-management system does not establish a compliant or working connection.",
      },
      {
        q: "Can it verify insurance?",
        a: "Real-time payor verification is not integrated. The flow can record a plan name and a question for the front desk. Your practice decides which additional details belong in its approved intake process and verifies coverage itself.",
      },
      {
        q: "What if a caller is in pain and needs to be seen today?",
        a: "Emergency calls can trigger a priority review flow; your practice confirms the earliest eligible slot and any configured alert path.",
      },
      {
        q: "Will it understand specialty terms?",
        a: "Yes. The model is tuned with dental vocabulary (apicoectomy, supernumerary, occlusion, etc.). For specialty referrals beyond your scope, the AI captures the request and routes it to your referral coordinator.",
      },
    ],
    callFaqs: [
      {
        q: "What if a new patient calls with a broken tooth and is in pain right now?",
        a: "During setup we can configure the call flow so a pain call gets flagged as urgent and routed for your practice to confirm the earliest eligible slot instead of the standard new-patient exam.",
      },
      {
        q: "Can it handle an existing patient rescheduling a cleaning?",
        a: "Yes. During setup we can configure the flow so existing patients skip the new-patient intake questions and go straight to rebooking the next cleaning.",
      },
      {
        q: "What about an Invisalign or cosmetic consult inquiry?",
        a: "The AI can request the consultation and flags the interest so your treatment coordinator can follow up prepared.",
      },
      {
        q: "Is this a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "Does it write appointments back into Dentrix or Open Dental automatically?",
        a: "Dentrix, Open Dental, EagleSoft, Curve Dental, and Denticon compatibility is assessed during setup; verify the account before promising a connection. For a practice-management system outside that list, we assess whether an authorized webhook path is available and test it before offering it.",
      },
      {
        q: "What happens on a treatment-plan question about work discussed at a prior visit?",
        a: "The AI takes down the details and routes the call to your treatment coordinator instead of trying to answer clinical or financial specifics itself.",
      },
      {
        q: "What if a caller has a general insurance benefits question rather than a new appointment?",
        a: "The AI captures the question and routes it as a callback during business hours instead of quoting coverage details on the call.",
      },
      {
        q: "When does a new patient get the intake form, before or after the visit?",
        a: "The AI can send an intake-form link after the appointment request is verified; your practice confirms the appointment and address details.",
      },
    ],
    whyChleb: [
      "New-patient capture is the single biggest revenue lever for a dental practice. We optimize for it.",
      "Insurance info captured on configured intake calls and queued for your front desk before the appointment",
      "Vertical-tuned vocabulary, not generic answering-service script",
      "Dentrix, Open Dental, EagleSoft, Curve, and Denticon compatibility assessed during setup; verify the account before promising a connection",
      "Configured missed-call follow-up for after-hours inquiries",
    ],
    relatedComparisons: [
      { competitor: "Smith.ai", slug: "smith-ai", note: "Compare the current Smith.ai offer with a configured dental call flow" },
      { competitor: "Goodcall", slug: "goodcall", note: "Compare the current Goodcall offer with a configured dental call flow" },
    ],
  },
  {
    slug: "plumbing",
    route: "/plumbing",
    vertical: "Plumbing",
    title: "AI Receptionist for Plumbing Companies | Chleb AI",
    metaDescription:
      "Chleb AI captures plumbing call details, service addresses and estimate requests. Review service-area, scheduling and urgent handoff rules before launch.",
    hero: {
      h1: "AI receptionist for plumbers. Capture urgent calls and estimate requests.",
      intro:
        "Capture the service address, reported problem and requested timing while your plumbers are on a job. Chleb configures urgent-request routing and routine estimate intake with your team. Dispatch availability, instructions and appointment confirmation follow the workflow your business approves and tests.",
    },
    directAnswer:
      "Capture the service address, reported problem and requested timing while your plumbers are on a job. Chleb configures urgent-request routing and routine estimate intake with your team. Dispatch availability, instructions and appointment confirmation follow the workflow your business approves and tests.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this plumbing call flow for plumbers in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "Why plumbers lose the calls that matter most",
      story:
        "Plumbing is the trade where the bottleneck is always the same: the owner is the only one who answers the phone, and the owner is always on a job. By the time you call back from a customer's basement, the burst-pipe lead has already requested your competitor. Chleb AI answers while you're working, qualifies the urgency, and routes the request for scheduling.",
      cost:
        "When the owner is the only one who can answer and the owner is on a job, peak-window emergency calls go unanswered and the caller can request the next plumber who picks up.",
    },
    sampleCall: {
      context: "Illustrative fictional scenario: Homeowner calls Saturday at 11am with a burst pipe.",
      transcript: [
  {
    "role": "caller",
    "text": "There is a leak under the kitchen sink. Can a plumber come today?"
  },
  {
    "role": "ai",
    "text": "I can take an urgent request for the plumbing team. What is the service address, and is water still leaking?"
  },
  {
    "role": "caller",
    "text": "824 Oak Lane. Yes, it is still leaking."
  },
  {
    "role": "ai",
    "text": "I will flag the reported leak for the team. They need to confirm availability and any visit charge; I cannot confirm a plumber is on the way."
  },
  {
    "role": "caller",
    "text": "My name is Alex. Please have them call me."
  },
  {
    "role": "ai",
    "text": "What callback number should I include, and are there access details the team needs?"
  }
],
    },
    aiKnows: {
      integrationNote: "These field-service systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, dispatch and calendar writes, duplicate handling, failed delivery, and the unanswered on-call path during setup.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      vocabulary: [
        "burst pipe, slab leak, sewer backup, water heater (gas vs electric vs tankless)",
        "drain cleaning vs hydro-jetting vs camera inspection",
        "fixture install (toilet, faucet, garbage disposal, water softener)",
        "permit-required work and code language",
        "after-hours and weekend rate structure",
      ],
      integrations: [
        "ServiceTitan, Housecall Pro, Jobber (compatibility assessed during setup; verify the account)",
        "FieldEdge, RazorSync, Workiz (compatibility assessed during setup; verify the account)",
        "Generic CRM via webhook (subject to endpoint access and a successful test)",
      ],
      callPatterns: [
        "Emergency dispatch (burst pipe, sewer backup, no-hot-water)",
        "Routine appointment (faucet replacement, water heater install, drain cleaning)",
        "Estimate request (sewer line, repipe, fixture upgrade)",
        "Warranty and follow-up callbacks",
        "Commercial vs residential routing",
      ],
    },
    stats: [
      { num: "configured after-hours coverage", label: "peak-window and weekend calls can be answered instead of hitting voicemail" },
      { num: "Live", label: "calls can be answered after hours instead of going to voicemail" },
    ],
    objections: [
      {
        q: "What if I want to take the call myself?",
        a: "Choose the forwarding and ring order with your team, then test answered and unanswered calls on the actual phone setup. Keep a documented way to return calls to your original line.",
      },
      {
        q: "Can it tell the difference between a real emergency and someone shopping around?",
        a: "Yes. The flow asks 1-2 qualifying questions (\"is water actively running, has it been shut off, how long has it been leaking\") and triages accordingly. Tire-kickers get an estimate flow; emergencies get flagged for same-day review.",
      },
      {
        q: "Will it know my pricing?",
        a: "Use only the pricing language your business approves, including when a quote needs an inspection. Test an in-scope question and an unknown service; the unknown request should go to your team without an invented price.",
      },
    ],
    callFaqs: [
      {
        q: "What does the AI do first on a burst-pipe call?",
        a: "It asks whether the main water valve has been shut off and walks the caller through doing it if not, then flags the call as urgent and can send your on-call plumber a configured summary so the team can dispatch.",
      },
      {
        q: "How does it separate a real emergency from someone price-shopping?",
        a: "It asks a couple of qualifying questions, whether water is actively running and how long it has been leaking. During setup we can configure the flow so real emergencies get flagged as urgent while price questions go to a standard estimate request.",
      },
      {
        q: "Can it route a commercial call differently than residential?",
        a: "Yes, during setup we can configure separate routing for commercial and residential calls, so a property manager's call does not get the same intake as a homeowner's.",
      },
      {
        q: "What does the AI ask on a sewer backup or no-hot-water call?",
        a: "The same questions as a burst-pipe call: whether water is actively running, whether it has been shut off, and how long it has been going on. It flags the call as urgent and can send your on-call plumber a configured summary so the team can dispatch.",
      },
      {
        q: "Is this a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "Do I need to leave ServiceTitan or Jobber to use this?",
        a: "No. ServiceTitan, Housecall Pro, Jobber, FieldEdge, RazorSync, and Workiz compatibility is assessed during setup; verify the account before promising a connection. The call summary and booking still go to your team; we confirm a generic CRM via webhook if you use something else.",
      },
      {
        q: "What about a fixture-install request, like a new water heater or garbage disposal?",
        a: "It can request it as a routine appointment, the same flow as a faucet replacement or drain cleaning, unless the caller describes an active leak or no-hot-water situation that needs the emergency flow instead.",
      },
    ],
    whyChleb: [
      "Configured for the plumber's specific bottleneck: you're on a job, the phone rings",
      "Emergency triage built in, not a generic intake script",
      "ServiceTitan, Housecall Pro, and Jobber compatibility assessed during setup; verify the account before promising a connection",
      "Configured missed-call follow-up can preserve an after-hours request for team review",
      "One monthly subscription; usage is billed separately at carrier cost",
    ],
    relatedComparisons: [
      { competitor: "Smith.ai", slug: "smith-ai", note: "Compare the current Smith.ai offer with a configured plumbing call flow" },
      { competitor: "Goodcall", slug: "goodcall", note: "Compare the current Goodcall offer with a configured plumbing call flow" },
      { competitor: "Vapi", slug: "vapi", note: "Build-it-yourself voice API, not a finished product" },
    ],
  },
  {
    slug: "auto-dealer",
    route: "/auto-dealer",
    vertical: "Auto Dealers",
    title: "AI Receptionist for Auto Dealerships | Chleb AI",
    metaDescription:
      "AI receptionist for dealership sales and service calls. Check booking, department routing, warm transfers and dealer-system access before forwarding calls.",
    hero: {
      h1: "AI receptionist for auto dealerships. Handle sales and service calls after hours.",
      intro:
        "Chleb AI can answer configured dealership calls after hours and whenever your team is busy, including after the BDC closes. It asks whether the caller needs sales, service, parts, or finance, and collects the vehicle or stock number they are asking about along with their contact details. Booking depends on your connected calendar and the appointment types approved during setup. Without a verified booking connection, it captures an appointment request for your team. It does not read your dealership's DMS inventory directly; your sales team confirms vehicle availability.",
    },
    directAnswer:
      "Chleb AI can answer configured dealership calls after hours and whenever your team is busy, including after the BDC closes. It asks whether the caller needs sales, service, parts, or finance, and collects the vehicle or stock number they are asking about along with their contact details. Booking depends on your connected calendar and approved appointment types. Without a verified booking connection, it captures an appointment request and sends your team a summary for follow-up. It does not read your dealership's DMS inventory directly; your sales team confirms vehicle availability.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this dealership call flow for dealers in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "Why dealerships leak leads on the phone",
      story:
        "Illustrative scenario: a 2025 Camry inquiry arrives after the BDC closes. If the call rolls to general voicemail, the buyer may try another dealership. A configured Chleb flow can qualify the caller's intent (sales, service, parts, finance), route the request, and request an appointment on a connected booking calendar after the write is verified.",
      cost:
        "A mis-routed or missed call can cost a sale, a service appointment, or a finance deal, and the caller often does not call back. Recording the request gives your team an opportunity to follow up; it does not guarantee a sale.",
    },
    sampleCall: {
      heading: "An illustrative dealership call",
      context: "Illustrative call at 8:42pm. The caller, vehicle and contact details are fictional.",
      transcript: [
        { role: "caller", text: "Hi, I'm interested in the 2025 Camry XSE I saw on your website. Is it still available?" },
        { role: "ai", text: "I can take down the details for our sales team to confirm. Do you have the stock number or the trim package details?" },
        { role: "caller", text: "It was the white one with the panoramic sunroof, the XSE trim I think." },
        { role: "ai", text: "Got it, the white XSE with the panoramic sunroof. I'll pass those details to our sales team to confirm the listing and availability before your visit. Are you looking to test drive, get a trade appraisal, or talk to someone about financing options?" },
        { role: "caller", text: "I'd like to test drive it tomorrow and also see what lease options look like." },
        { role: "ai", text: "I can request a test drive with our sales team. The available times need to be confirmed on the connected calendar. Which window should I pass along?" },
        { role: "caller", text: "Let's do 1pm." },
        { role: "ai", text: "I can add your finance question to the request for the F&I team. Their availability and lease options need to be confirmed by the dealership. Can I grab your name, phone, and a current car you might trade?" },
        { role: "caller", text: "Mark Davies, 813 555 0182, 2019 Honda Accord 60K miles." },
        { role: "ai", text: "I'll send the trade details to your appraisal team for review. The dealership will confirm the appraisal path and any appointment details. Anything else should I include?" },
      ],
    },
    aiKnows: {
      vocabulary: [
        "stock number collection, relayed to your sales team to confirm availability",
        "trim levels, packages, MSRP vs invoice language",
        "test drive vs appraisal vs F&I appointment types",
        "lease vs finance vs cash buyer routing",
        "service appointment types (oil change, recall, warranty, body shop)",
        "trade-in qualification (year, make, model, mileage, condition)",
      ],
      integrations: [
        "VinSolutions (compatibility listed for review; verify the dealership account)",
        "DealerSocket (compatibility listed for review; verify the dealership account)",
        "Elead (compatibility listed for review; verify the dealership account)",
        "ProMax, AutoAlert, Reynolds Contact Management (compatibility listed for review; verify the dealership account)",
        "Webhook delivery, subject to endpoint access and a successful test",
      ],
      integrationNote: "These are systems to assess, not verified Chleb connectors. Confirm account permissions, supported read and write operations, and the receiving record before promising an integration. A phone transfer does not itself deliver a transcript or update a dealer CRM.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      callPatterns: [
        "Sales inquiry (specific stock vs general browsing, route to BDC or sales floor)",
        "Service appointment (vehicle year, make, and model, service type, lane-based routing)",
        "Parts inquiry (part-detail collection or counter callback)",
        "Finance and F&I (lease options, current finance question, route to F&I manager)",
        "Body shop and collision center routing",
        "Management escalation",
      ],
    },
    stats: [
      { num: "24/7", label: "after-hours coverage can be configured around your staffed hours" },
    ],
    objections: [
      {
        q: "Does it integrate with my dealer CRM?",
        a: "Compatibility is checked per dealership. The integration review lists VinSolutions, DealerSocket, Elead, ProMax, and AutoAlert for verification, while anything outside that set needs a separate compatibility decision or a separately tested webhook handoff.",
      },
      {
        q: "What about live inventory lookup?",
        a: "The AI collects the stock number or vehicle the caller asks about and sends it to your sales team to confirm availability. Your team can offer alternatives if a specific stock number is gone, in transit, or recently sold.",
      },
      {
        q: "Can it transfer a hot prospect to a live salesperson during business hours?",
        a: "A warm transfer can be configured during staffed hours, but test whether the person answers and receives the call details separately. If the recipient does not answer, retain the name, vehicle interest, and intent for the designated team and use the approved callback or appointment path.",
      },
      {
        q: "Service department wants its own flow. Possible?",
        a: "Service calls can use a separate intake (VIN or year, make, and model, service type, customer pay vs warranty, advisor assignment). The appointment must appear at an eligible time in the correct service calendar, without duplicates, in a test before direct booking is promised.",
      },
    ],
    callFaqs: [
      {
        q: "What happens when a caller asks about a specific vehicle or stock number?",
        a: "The AI collects the stock number or vehicle details along with the caller's contact info and sends it to your sales team, who confirm availability and follow up with next steps.",
      },
      {
        q: "What does the AI do with a service call about a recall?",
        a: "During setup we can configure a separate service intake for recall calls, so the AI collects the VIN or year, make, and model. The dealership must confirm recall eligibility and a test booking must appear once in the connected calendar before booking is promised.",
      },
      {
        q: "How does an F&I question get handled on a sales call?",
        a: "The AI can flag the finance question for your F&I team. The manager's availability and lease or finance options still need dealership confirmation; the illustrative call is not proof of a completed handoff.",
      },
      {
        q: "Is this a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "What if a caller asks for a manager or wants to escalate a complaint?",
        a: "The AI captures the details and routes the call to your management escalation queue instead of trying to resolve it itself.",
      },
      {
        q: "What does the AI do with a parts-department call?",
        a: "It captures the part or vehicle detail the caller is asking about, along with their contact information, and routes it to your parts department instead of trying to look up inventory itself.",
      },
      {
        q: "How does a trade-in appraisal request get handled?",
        a: "The AI can collect the trade vehicle's year, make, model, and mileage and send a request to your appraisal team. The dealership must confirm receipt, eligibility, and timing; the illustrative call is not proof that an appraisal was queued.",
      },
      {
        q: "Does it handle a body shop or collision repair call differently than a sales or service call?",
        a: "A body shop or collision inquiry can be routed to the designated body shop or collision team, with vehicle details and caller contact information. Run a synthetic delivery test before promising that a specific team received it.",
      },
    ],
    whyChleb: [
      "Configured department routing with a human fallback when the first path does not answer",
      "Stock number capture prepared for sales-team availability confirmation",
      "After-hours sales capture when the BDC is offline, subject to the configured queue",
      "Service drive scheduling with advisor and lane rules verified before direct booking",
      "VinSolutions, DealerSocket, and Elead compatibility reviewed per dealership account",
    ],
    relatedComparisons: [
      { competitor: "Goodcall", slug: "goodcall", note: "Publishes per-agent plans at $79, $129, and $299 for 100, 500, and 2,000 unique customers, with tier-specific overage and enterprise volume pricing; dealer-specific tuning still has to be configured" },
      { competitor: "Smith.ai", slug: "smith-ai", note: "Human-agent overflow; per-call pricing rough at dealership volumes" },
    ],
  },
  {
    slug: "salon",
    route: "/salon",
    vertical: "Salons & Barbershops",
    title: "AI Receptionist for Salons and Barbershops | Chleb AI",
    metaDescription:
      "Chleb AI captures salon appointment requests, service choices and stylist preferences. Review calendar access, durations and cancellation rules before launch.",
    hero: {
      h1: "AI receptionist for salons and barbershops. Handle appointment requests while you work.",
      intro:
        "Collect the service, preferred stylist and requested time without interrupting work at the chair. Chleb configures the intake around your service durations and booking policies. Showing availability, changing an appointment or collecting a deposit depends on the connected system and a tested workflow.",
    },
    directAnswer:
      "Collect the service, preferred stylist and requested time without interrupting work at the chair. Chleb configures the intake around your service durations and booking policies. Showing availability, changing an appointment or collecting a deposit depends on the connected system and a tested workflow.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this salon and barbershop call flow for shops in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "Why salons leak appointments to voicemail",
      story:
        "The bottleneck in a salon is always the same: the chair owner is the receptionist, and the chair owner is always in a service. Calls that come in during a cut go to voicemail. Walk-ins fill the gap, but walk-ins don't pre-book color, which is your highest-margin service. Chleb AI picks up while you're in a service, qualifies the request (stylist preference, service type, time window), and can request the appointment on your connected booking calendar.",
      cost:
        "Missed calls during peak chair hours do not become voicemails clients wait on, they become bookings at the salon down the street. Answering live, even mid-service, keeps that client in a request queue instead.",
    },
    sampleCall: {
      context: "Illustrative fictional scenario: Caller Saturday at 10:30am asking about Saturday availability.",
      transcript: [
  {
    "role": "caller",
    "text": "Can I get a cut and partial balayage with Jessica on Saturday?"
  },
  {
    "role": "ai",
    "text": "I can take that request for the salon. What time on Saturday would you prefer?"
  },
  {
    "role": "caller",
    "text": "Around two in the afternoon."
  },
  {
    "role": "ai",
    "text": "I will note two o’clock as your preference. The salon needs to confirm Jessica’s availability and enough time for both services. What name should I use?"
  },
  {
    "role": "caller",
    "text": "Anna."
  },
  {
    "role": "ai",
    "text": "Thank you, Anna. What callback number should the salon use to confirm the time and explain its pricing and deposit policy?"
  }
],
    },
    aiKnows: {
      integrationNote: "These booking systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, service and staff availability, booking writes, duplicate handling, and failed delivery during setup.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      vocabulary: [
        "cut + style, balayage, highlights, ombre, color correction, glossing, toner",
        "single process vs double process, partial vs full",
        "blowout, extensions, perm or relaxer, keratin treatment",
        "men's cut variations (fade, taper, beard trim, hot towel)",
        "service time math per stylist (some take 2.5 hr for what others do in 3.5 hr)",
        "deposit and cancellation policy enforcement",
      ],
      integrations: [
        "Square Appointments (compatibility assessed during setup; verify the account)",
        "Booksy (compatibility assessed during setup; verify the account)",
        "Vagaro (compatibility assessed during setup; verify the account)",
        "Fresha, GlossGenius (compatibility assessed during setup; verify the account)",
        "Booking-system webhook path, subject to account access and a successful test",
      ],
      callPatterns: [
        "New client first visit (stylist match + service consultation booking)",
        "Existing client recall (preferred stylist + standard service)",
        "Specific stylist request with availability check",
        "Wedding and event party booking (block multiple chairs)",
        "Walk-in availability check (your connected booking calendar)",
        "Product purchase and curbside pickup",
      ],
    },
    stats: [
      { num: "configured after-hours coverage", label: "calls answered while every chair is busy" },
    ],
    objections: [
      {
        q: "Can it match callers to the right stylist?",
        a: "With a tested calendar connection, the flow can check the configured stylist availability and service duration. Otherwise, it records the preferred stylist and service for salon review. Test requests for unfamiliar services and unavailable staff before launch.",
      },
      {
        q: "What about cancellation and no-show policies?",
        a: "The AI can state your policy and request a deposit through a connected booking flow if that account supports it; verify the payment and cancellation behavior during setup.",
      },
      {
        q: "Can it sell retail products over the phone?",
        a: "Limited. The AI can collect the product request and route it to your team for a curbside pickup booking, but won't take credit card payments over the phone. A salon can route those to an approved invoice or payment link; verify the delivery path during setup.",
      },
    ],
    callFaqs: [
      {
        q: "What if a caller doesn't have a preferred stylist?",
        a: "The AI asks a couple of service questions. It can be set up to offer a stylist whose specialty fits, a color specialist for a balayage request or a curl specialist for a curly cut.",
      },
      {
        q: "Can it book a multi-chair wedding-party appointment?",
        a: "Treat a multi-chair booking as a separate setup requirement. Test staff availability, service duration and conflicts together before allowing confirmation. Otherwise, collect the party details for the salon team.",
      },
      {
        q: "What happens on a walk-in availability question?",
        a: "The AI can check an approved booking calendar and offer a configured available window; verify the calendar response during setup.",
      },
      {
        q: "Does it work with the booking software I already use?",
        a: "Square Appointments, Booksy, Vagaro, Fresha, and GlossGenius compatibility is assessed during setup; verify the account before promising a connection. If you use something else, we assess whether an authorized webhook path is available and test it before offering it.",
      },
      {
        q: "Is this a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "Does it handle a men's cut or barbershop-specific request differently?",
        a: "Yes. It recognizes men's cut variations like a fade, taper, beard trim, or hot towel finish, and can request the appointment length that service needs.",
      },
      {
        q: "Does the AI quote color service pricing over the phone?",
        a: "The sample leaves pricing and deposit terms for salon confirmation. If you want approved prices read out, define the exact service scope and test what happens when the requested work falls outside it.",
      },
    ],
    whyChleb: [
      "Stylist-aware booking, not generic time-slot picker",
      "Service-time math respects how long each stylist takes",
      "Configured cancellation and deposit policy checked before launch",
      "Square, Booksy, Vagaro, and Fresha compatibility assessed during setup; verify the account before promising a connection",
      "Configured missed-call follow-up can preserve evening requests overnight",
    ],
    relatedComparisons: [
      { competitor: "Smith.ai", slug: "smith-ai", note: "Compare the current Smith.ai offer with a configured salon call flow" },
      { competitor: "Goodcall", slug: "goodcall", note: "Compare the current Goodcall offer with a configured salon call flow" },
    ],
  },
  {
    slug: "med-spa",
    route: "/med-spa",
    vertical: "Med Spas",
    title: "AI Receptionist for Med Spas | Chleb AI",
    metaDescription:
      "Chleb AI captures med spa consultation requests and routes treatment questions to staff. Review intake fields, provider calendars and handoffs before launch.",
    hero: {
      h1: "AI receptionist for med spas. Route consultation requests to your team.",
      intro:
        "Capture the treatment a caller is asking about and their preferred consultation time after the front desk closes. Chleb configures approved intake and staff routing for your practice. Qualified staff decide suitability and treatment plans; calendar access and appointment confirmation need setup-specific testing.",
    },
    directAnswer:
      "Capture the treatment a caller is asking about and their preferred consultation time after the front desk closes. Chleb configures approved intake and staff routing for your practice. Qualified staff decide suitability and treatment plans; calendar access and appointment confirmation need setup-specific testing.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this med spa call flow for practices in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "Why med spas lose consultation revenue overnight",
      story:
        "Med spa buying intent peaks evenings and weekends, when working professionals can actually call. Your front desk closes at 6pm. Calls roll to voicemail. Chleb AI answers the call, qualifies the treatment interest (Botox, filler, laser, body contouring, membership), and can request the consultation on your connected booking calendar.",
      cost:
        "Med spa buying intent peaks evenings and weekends, right when your front desk is closed. A live pickup after hours gives those callers a way to book on the spot instead of calling around.",
    },
    sampleCall: {
      context: "Illustrative fictional scenario: Caller at 8:34pm asking about Botox.",
      transcript: [
  {
    "role": "caller",
    "text": "I would like to ask about Botox. Do I need a consultation?"
  },
  {
    "role": "ai",
    "text": "I can request a consultation with the practice. A qualified team member will answer treatment and suitability questions. What day would you prefer?"
  },
  {
    "role": "caller",
    "text": "Saturday, if possible."
  },
  {
    "role": "ai",
    "text": "I will note Saturday as your preference. The practice needs to confirm the consultation time. What first name should I use?"
  },
  {
    "role": "caller",
    "text": "Jordan."
  },
  {
    "role": "ai",
    "text": "Thank you, Jordan. What callback number should the practice use? I can also note any questions you want the team to discuss with you."
  }
],
    },
    aiKnows: {
      integrationNote: "These booking or practice systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, approved intake fields, privacy requirements, booking writes, duplicate handling, and failed delivery during setup.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      vocabulary: [
        "Botox, Dysport, Xeomin, Jeuveau, units, glabella, frontalis, orbicularis oculi",
        "filler (Juvederm, Restylane, RHA, Sculptra, lip, cheek, jawline, chin)",
        "laser hair removal (Diolase, Soprano, GentleMax, package vs single)",
        "body contouring (CoolSculpting, EmSculpt, BBL, sculpsure)",
        "facials, microneedling, PRP, IV therapy, hormone optimization",
        "membership vs single-treatment pricing, package discounts",
      ],
      integrations: [
        "Boulevard (compatibility assessed during setup; verify the account)",
        "Aesthetics Pro (compatibility assessed during setup; verify the account)",
        "Jane App (compatibility assessed during setup; verify the account)",
        "Vagaro, Mindbody (compatibility assessed during setup; verify the account)",
        "Practice-system webhook path, subject to account access and a successful test",
      ],
      callPatterns: [
        "First-time consultation booking (treatment interest + injector match)",
        "Returning client maintenance (same treatment, same injector, next slot)",
        "Membership inquiry (sales-qualified handoff)",
        "Package upgrade or treatment plan question",
        "Pricing inquiry (transparent quote or callback)",
        "Cancellation or reschedule",
      ],
    },
    stats: [
      { num: "configured after-hours coverage", label: "consultation inquiries answered after the front desk closes" },
      { num: "Request", label: "captured for review instead of a voicemail callback" },
    ],
    objections: [
      {
        q: "Will the AI know our injectors and their specialties?",
        a: "Your practice can provide approved provider and service-routing rules. Availability checks require a tested calendar connection. Qualified staff decide which provider and treatment are appropriate; otherwise the flow records a consultation request for review.",
      },
      {
        q: "Can it handle questions about contraindications?",
        a: "Route these questions to qualified practice staff. Collect caller-reported health details only through the intake fields and access, retention and delivery process the practice has approved. The receptionist does not determine treatment suitability.",
      },
      {
        q: "What about pricing transparency?",
        a: "Configurable. Some practices want their published prices stated on the call. Others prefer the caller to hear a range and book the consultation for an exact figure. The AI follows your preference.",
      },
      {
        q: "Membership upsell?",
        a: "Yes. If a caller asks about repeat treatments or pricing, the AI surfaces your membership program and routes membership-interested callers to a sales-qualified consultation flow.",
      },
    ],
    callFaqs: [
      {
        q: "What happens on a returning-client maintenance call?",
        a: "During setup we can configure the flow so a returning client can request the same treatment with the same injector at the next available slot, instead of the full new-client consultation.",
      },
      {
        q: "What if the caller asks about a membership instead of a single treatment?",
        a: "The AI can be set up to mention your membership program when a caller asks about repeat treatments, and can request a consultation for the caller to learn more.",
      },
      {
        q: "What if a caller mentions pregnancy or blood thinners?",
        a: "The AI flags it for your injector to review before the appointment, and does not give medical advice on the call.",
      },
      {
        q: "Is Chleb itself a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "Does it work with Boulevard or Aesthetics Pro?",
        a: "Boulevard, Aesthetics Pro, Jane App, Vagaro, and Mindbody compatibility is assessed during setup; verify the account before promising a connection. For an EMR outside that list, we assess whether an authorized webhook path is available and test it before offering it.",
      },
      {
        q: "Does the AI recognize different neurotoxin brands, like Dysport or Xeomin, not just Botox?",
        a: "Yes. The intake vocabulary covers Botox, Dysport, Xeomin, and Jeuveau, so it can note which one the caller is asking about before booking the consultation.",
      },
      {
        q: "How does the AI handle a laser hair removal inquiry?",
        a: "For a laser inquiry the receptionist asks which treatment the caller is interested in and whether they are asking about a package or a single session, then can request the consultation on your connected booking calendar.",
      },
      {
        q: "Does the AI give pre-treatment instructions like avoiding alcohol or ibuprofen before a consultation?",
        a: "It routes pre-visit questions to the practice or injector for review and does not give medical advice on the call.",
      },
    ],
    whyChleb: [
      "Vertical-tuned aesthetic vocabulary, not generic answering service",
      "Injector-specific routing and availability awareness",
      "Practice-approved intake and staff review of treatment questions",
      "Boulevard, Aesthetics Pro, and Jane App compatibility assessed during setup; verify the account before promising a connection",
      "Membership and package upsell built into the flow when configured",
    ],
    relatedComparisons: [
      { competitor: "Smith.ai", slug: "smith-ai", note: "Compare the current Smith.ai offer with a configured med-spa call flow" },
      { competitor: "Goodcall", slug: "goodcall", note: "Compare the current Goodcall offer with a configured med-spa call flow" },
    ],
  },
  {
    slug: "lawn-care",
    route: "/lawn-care",
    vertical: "Lawn Care & Landscaping",
    title: "AI Receptionist for Lawn Care Companies | Chleb AI",
    metaDescription:
      "Chleb AI captures lawn-care quote requests, property details and service preferences. Review service-area, estimate scheduling and handoffs before launch.",
    hero: {
      h1: "AI receptionist for lawn care. Capture quote requests while your crew works.",
      intro:
        "Collect the property address, requested service and preferred estimate window while your crew is out on a property. Chleb configures intake and follow-up with your business. Your team approves price information, service coverage and the steps that turn a request into a scheduled estimate.",
    },
    directAnswer:
      "Collect the property address, requested service and preferred estimate window while your crew is out on a property. Chleb configures intake and follow-up with your business. Your team approves price information, service coverage and the steps that turn a request into a scheduled estimate.",
    tampa:
      "Chleb AI is based in Tampa, Florida, and configures this lawn care call flow for companies in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    pain: {
      headline: "Why lawn care companies lose new accounts in the spring rush",
      story:
        "Lawn care calls come in seasonal waves: every spring's first warm Saturday, every fall after the first leaf drop. The owner is on a property running a crew. Calls come in. They roll to voicemail. By Monday morning, many of those callers have hired the lawn service that called them back. Chleb AI answers the call, qualifies the request (recurring service, one-time cleanup, landscape estimate), and can request the estimate or service.",
      cost:
        "Spring-rush calls that hit voicemail do not wait around, homeowners call the next lawn service instead. A quick text back after a missed call gives them a way to book with you first.",
    },
    sampleCall: {
      context: "Illustrative fictional scenario: Homeowner calls Saturday at 11am during peak spring season.",
      transcript: [
  {
    "role": "caller",
    "text": "I need a quote for mowing and a one-time cleanup."
  },
  {
    "role": "ai",
    "text": "I can collect the details for an estimate request. What is the property address and approximate lot size?"
  },
  {
    "role": "caller",
    "text": "512 Cedar Street, about a third of an acre."
  },
  {
    "role": "ai",
    "text": "Thank you. Is there a day you would prefer for the estimate?"
  },
  {
    "role": "caller",
    "text": "Tuesday morning. My name is James."
  },
  {
    "role": "ai",
    "text": "I will note Tuesday morning as your preference, James. The team will need to confirm the estimate time and scope. What callback number should they use?"
  }
],
    },
    aiKnows: {
      integrationNote: "These job-management systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, estimate or job-request writes, duplicate handling, failed delivery, and the team handoff during setup.",
      integrationGuide: { href: "/integrations/zapier-and-webhooks", label: "What to verify in a custom integration" },
      vocabulary: [
        "recurring mow vs one-time mow vs cleanup",
        "lot size language (quarter, third, half, full acre)",
        "edging, trimming, blowing, bagging vs mulching",
        "fertilization programs (4-step, 6-step, organic)",
        "landscaping estimate (mulch, sod, plant install, hardscape)",
        "tree services (trim, removal, stump grinding)",
        "snow removal (seasonal switch for northern markets)",
      ],
      integrations: [
        "Jobber (compatibility assessed during setup; verify the account)",
        "Service Autopilot (compatibility assessed during setup; verify the account)",
        "LawnPro, RealGreen, ServiceTitan (compatibility assessed during setup; verify the account)",
        "Generic CRM via webhook (subject to endpoint access and a successful test)",
      ],
      callPatterns: [
        "Recurring service inquiry (mow + maintenance program signup)",
        "One-time service (cleanup, leaf removal, hedge trim)",
        "Landscape estimate request (route to estimator)",
        "Tree work (separate estimate flow, often with liability discussion)",
        "Seasonal switch (snow removal in northern markets)",
        "Existing customer schedule change",
      ],
    },
    stats: [
      { num: "Follow-up", label: "configured after a missed call so the homeowner can respond" },
    ],
    objections: [
      {
        q: "Can it quote accurately without seeing the property?",
        a: "We configure your published price band per lot size (quarter, third, half, full acre) so the AI gives a quote range. The exact number comes from the in-person estimate, which the AI can request. The team must confirm the final scope and price.",
      },
      {
        q: "What if a caller asks about something I don't do (like irrigation install)?",
        a: "The AI says so and offers what you do offer. We configure your scope of service during setup. If a caller asks about something out-of-scope, the AI captures the request and routes it to your office for a callback if you want to refer it out, or politely declines if not.",
      },
      {
        q: "Will it handle seasonal switch (mowing to snow removal)?",
        a: "Yes. The intake script can switch with the season. We swap configurations during the shoulder months.",
      },
    ],
    callFaqs: [
      {
        q: "What if a caller wants a one-time cleanup, not recurring service?",
        a: "During setup we can configure a separate path for one-time cleanup, leaf removal, and hedge-trim requests, so the AI does not push them into the recurring mow signup.",
      },
      {
        q: "How does it handle a tree-work request?",
        a: "During setup tree-work requests can be routed through their own estimate flow, and the AI flags the liability discussion for your office before the visit is confirmed.",
      },
      {
        q: "What happens when a caller wants to switch from mowing to snow removal in fall?",
        a: "During setup we can configure a seasonal switch for northern markets, so the same caller can move from a mowing schedule to snow removal without starting over.",
      },
      {
        q: "Is this a per-call answering service or a flat monthly subscription?",
        a: "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call.",
      },
      {
        q: "Do I need to switch off Jobber or Service Autopilot?",
        a: "No. Jobber, Service Autopilot, LawnPro, RealGreen, and ServiceTitan compatibility is assessed during setup; verify the account before promising a connection. For another system, we assess account access and a testable data path before promising a connection.",
      },
      {
        q: "What if a caller wants a fertilization program instead of mowing?",
        a: "The AI can be configured to recognize a fertilization program request, like a 4-step, 6-step, or organic program, and can request it as its own service instead of folding it into a mowing quote.",
      },
      {
        q: "How does a landscaping estimate, like mulch, sod, or a plant install, get handled versus a mow quote?",
        a: "It routes a landscaping estimate request, mulch, sod, plant install, or hardscape work, to your estimator instead of using the lot-size mow-pricing questions.",
      },
      {
        q: "Can a caller start with a one-time estimate and add a recurring mow schedule later?",
        a: "The sample captures mowing and cleanup interest for an estimate request. Recurring service starts only after your team confirms scope, price and the schedule. Any follow-up message or calendar update needs its own delivery check.",
      },
    ],
    whyChleb: [
      "Configured for the peak-spring call-volume problem specifically",
      "Lot-size-aware quoting in a price range, not a guess",
      "Jobber and Service Autopilot compatibility assessed during setup; verify the account before promising a connection",
      "Configured missed-call follow-up can preserve Sunday-night estimate requests for team review",
      "Seasonal switch for northern markets (mow to snow)",
    ],
    relatedComparisons: [
      { competitor: "Smith.ai", slug: "smith-ai", note: "Compare the current Smith.ai offer with a configured lawn-care call flow" },
      { competitor: "Goodcall", slug: "goodcall", note: "Compare the current Goodcall offer with a configured lawn-care call flow" },
    ],
  },
  // Roofing and law firms, added 2026-09-22 from seo/drafts/vertical-*.json
  // (written and fact-checked against seo/facts.md).
  {
    "slug": "roofing",
    "route": "/roofing",
    "vertical": "Roofing",
    "title": "AI Receptionist for Roofing Contractors | Chleb AI",
    "metaDescription": "Chleb AI captures roofing inspection and urgent leak requests after hours. Review service coverage, scheduling access and crew handoff rules before launch.",
    "hero": {
      "h1": "AI receptionist for roofing contractors. Capture inspection and urgent leak requests.",
      "intro": "Collect the property address, reported roof problem and access notes when your crew cannot answer. Chleb configures routine inspection intake and urgent-request routing with your business. Your team determines crew availability and safe access; a saved request is not a confirmed dispatch."
    },
    "directAnswer": "Collect the property address, reported roof problem and access notes when your crew cannot answer. Chleb configures routine inspection intake and urgent-request routing with your business. Your team determines crew availability and safe access; a saved request is not a confirmed dispatch.",
    "tampa": "Chleb AI is based in Tampa, Florida, and configures this roofing call flow for contractors in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    "pain": {
      "headline": "What missed roofing calls actually cost you",
      "story": "Roofing calls spike right when a storm rolls through, and that is exactly when a crew is hardest to reach. A homeowner with an active leak or a tree limb through the roof is calling three contractors in a row, and the first one to pick up usually gets the job. Voicemail loses that job. Chleb AI answers that call, triages the emergency, and can request the visit on your calendar.",
      "cost": "A roofing call that reaches voicemail during a storm may send the caller to another crew. A configured follow-up can preserve the request for your team to review."
    },
    "sampleCall": {
      "context": "Illustrative fictional scenario: Homeowner calls at 9:20pm during a tropical storm. Water coming through the ceiling.",
      "transcript": [
  {
    "role": "caller",
    "text": "Water is coming through our ceiling during the storm. Can someone help tonight?"
  },
  {
    "role": "ai",
    "text": "I can take an urgent request for the roofing team. What is the property address?"
  },
  {
    "role": "caller",
    "text": "918 Palmetto Court. My name is Marcus."
  },
  {
    "role": "ai",
    "text": "Thank you, Marcus. Where are you seeing the leak, and are there access details you want the crew to know?"
  },
  {
    "role": "caller",
    "text": "The living room ceiling. There is a locked side gate."
  },
  {
    "role": "ai",
    "text": "I will include those details. The crew must decide whether and when they can attend; I cannot confirm a visit tonight. What callback number should they use?"
  }
]
    },
    "aiKnows": {
      "vocabulary": [
        "active leak, tarp emergency, wind damage, hail damage, missing shingles",
        "shingle, tile, metal, flat and low-slope roofing systems",
        "insurance claim inspection vs out-of-pocket estimate",
        "service area boundaries (you tell us, we won't quote outside them)",
        "storm-season surge patterns and after-hours dispatch",
        "re-roof vs repair vs full inspection requests"
      ],
      "integrationNote": "Chleb has not tested the proposed Jobber workflow end to end and does not claim a live Jobber connector. The example creates a request, not a scheduled job or an assigned crew. We check compatibility and scope for your software before promising an integration.",
      "integrationGuide": { "href": "/integrations/zapier-and-webhooks#request-is-not-dispatch", "label": "Read the proposed Jobber workflow and its limits" },
      "integrations": [
        "Jobber (proposed workflow; not tested end to end)",
        "JobNimbus (ask on the integration check)",
        "AccuLynx (ask on the integration check)",
        "Generic CRM via webhook (subject to endpoint access and a successful test) for shops on custom stacks",
        "Insurance carrier portals (ask on the integration check)"
      ],
      "callPatterns": [
        "Emergency tarp and leak dispatch (priority booking, access notes for the on-call crew)",
        "Insurance claim inspection requests (collect claim status, adjuster info, schedule inspector)",
        "Storm damage estimate requests (collect roof type and age, schedule estimator)",
        "Routine re-roof consultations (next available, lead-time aware)",
        "Warranty and follow-up requests (collect reported install date for office review)"
      ]
    },
    "stats": [
      {
        "num": "configured after-hours coverage",
        "label": "after-hours and storm calls can be answered instead of hitting voicemail"
      }
    ],
    "objections": [
      {
        "q": "Will it sound like a robot to my customers?",
        "a": "Use a trial call with your business wording to judge the voice and responses. Test a human handoff separately, including what happens when nobody answers and what caller details reach the recipient."
      },
      {
        "q": "How do we keep requests within the crew’s availability?",
        "a": "We configure the calendar with your real service area, real on-call schedule, and real availability windows. Test unavailable slots and conflicts on the actual calendar before allowing appointment confirmation. If a caller asks for something out-of-bounds, the AI takes the message and routes it to a human queue."
      },
      {
        "q": "Do I have to switch CRMs?",
        "a": "Chleb runs on HighLevel. Whether you can keep your current CRM depends on the workflow and compatibility check. We do not claim a live Jobber connector: the proposed Zapier workflow is untested and creates a request, not a scheduled job. JobNimbus and AccuLynx compatibility also needs review before we promise a connection."
      },
      {
        "q": "What about Spanish-speaking callers?",
        "a": "We're US English first today. Bilingual handoff is on the roadmap. If Spanish is critical to your market, talk to us about timeline before signing."
      }
    ],
    "callFaqs": [
      {
        "q": "What happens if a caller reports an active leak during a storm?",
        "a": "The AI flags the call as urgent and can send a configured summary to your on-call contact, including any access notes the caller gives, so your team can dispatch a crew."
      },
      {
        "q": "Can it tell a routine estimate request from a real storm emergency?",
        "a": "Yes. It asks a triage question first, then can request a routine estimate for the next available slot or flags an active leak or tarp emergency as urgent for your team."
      },
      {
        "q": "What does it do with an insurance claim inspection call?",
        "a": "It collects the claim status and adjuster information, then can request the inspector on your connected booking calendar during the same call."
      },
      {
        "q": "Is this a per-call answering service or a flat monthly subscription?",
        "a": "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call."
      },
      {
        "q": "What happens on a warranty or follow-up call about a past re-roof?",
        "a": "It can collect the caller’s reported installation date and reason for calling, then route the request to your office. Reading an installation record or confirming warranty eligibility requires a separately verified system connection."
      },
      {
        "q": "Does the AI quote the after-hours call-out fee on the phone?",
        "a": "Your team chooses whether the receptionist may state an approved fee or must leave that question for staff. Test the exact wording and the fallback when a fee is unknown; the sample request does not establish a price."
      }
    ],
    "whyChleb": [
      "Configured voice, follow-up and CRM workflow in one bundle",
      "Vertical-tuned out of the box, not configured generically",
      "Keeps your existing business number",
      "Missed-call follow-up can be configured and tested",
      "One monthly subscription; usage is billed separately at carrier cost"
    ],
    "relatedComparisons": [
      {
        "competitor": "Smith.ai",
        "slug": "smith-ai",
        "note": "Compare the current Smith.ai offer with a configured roofing call flow"
      },
      {
        "competitor": "Goodcall",
        "slug": "goodcall",
        "note": "Compare the current Goodcall offer with a configured call flow"
      }
    ]
  },
  {
    "slug": "legal",
    "route": "/legal",
    "vertical": "Law Firms",
    "title": "AI Receptionist for Law Firms | Chleb AI",
    "metaDescription": "Chleb AI captures law-firm inquiries and routes existing-client calls. Staff handle conflicts, representation decisions and consultation confirmation.",
    "hero": {
      "h1": "AI receptionist for law firms. Route new inquiries and existing-client calls.",
      "intro": "Collect initial inquiry details and route existing clients to the appropriate contact while your team is unavailable. Chleb configures intake and handoffs around your practice rules. Firm staff perform conflict checks, decide whether to accept a matter and confirm consultations; the receptionist does not provide legal advice."
    },
    "directAnswer": "Collect initial inquiry details and route existing clients to the appropriate contact while your team is unavailable. Chleb configures intake and handoffs around your practice rules. Firm staff perform conflict checks, decide whether to accept a matter and confirm consultations; the receptionist does not provide legal advice.",
    "tampa": "Chleb AI is based in Tampa, Florida, and configures this law firm call flow for attorneys in Tampa and across the United States. The published prices are $297 a month for the CRM, $899 a month for AI Receptionist + Chatbot, and $997 a month for the Front Office Bundle.",
    "pain": {
      "headline": "What missed new-matter calls actually cost a firm",
      "story": "A prospective client calls a law firm at 7pm after being served papers, gets voicemail, and calls the next firm on the list. Law firms lose new-matter calls to voicemail every week, and the caller rarely waits for a callback when the matter feels urgent. Chleb AI answers that call, takes the intake details needed to screen for conflicts, and can request the consultation on your calendar.",
      "cost": "A new-matter call that hits voicemail after hours is a consultation the caller can book with a different firm before your office opens. Answering live and booking the consult on the call keeps that intake from leaking overnight."
    },
    "sampleCall": {
      "context": "Illustrative fictional scenario: Caller at 7:40pm asking about a new personal injury matter.",
      "transcript": [
  {
    "role": "caller",
    "text": "I would like to speak with someone about a new matter."
  },
  {
    "role": "ai",
    "text": "I can take an inquiry for the intake team. What general type of matter is it, and what name should I use?"
  },
  {
    "role": "caller",
    "text": "A contract dispute. My name is Taylor."
  },
  {
    "role": "ai",
    "text": "Thank you, Taylor. The firm needs to review the inquiry and any potential conflicts before confirming a consultation. Please save detailed documents for the intake process the team provides."
  },
  {
    "role": "caller",
    "text": "Could they call me tomorrow?"
  },
  {
    "role": "ai",
    "text": "I can note that preference. What callback number should the intake team use? This request does not confirm that the firm represents you."
  }
]
    },
    "aiKnows": {
      "integrationNote": "Named systems are compatibility candidates, not verified Chleb connectors. Confirm account permissions, approved intake fields, privacy requirements, calendar writes, duplicate handling, and the staff handoff during setup.",
      "integrationGuide": { "href": "/integrations/zapier-and-webhooks", "label": "What to verify in a custom integration" },
      "vocabulary": [
        "practice areas: personal injury, family law, criminal defense, estate planning, business litigation",
        "new matter vs existing matter, retainer vs contingency vs hourly",
        "conflict check intake (opposing party names, related entities, prior representation)",
        "statute of limitations urgency flags (caller-reported dates only, never legal advice)",
        "consultation types: free consult, paid consult, retained-client call",
        "court schedule language (hearing, deposition, filing deadline)"
      ],
      "integrations": [
        "Generic CRM via webhook (subject to endpoint access and a successful test)",
        "Clio (ask on the integration check)",
        "MyCase (ask on the integration check)",
        "PracticePanther (ask on the integration check)"
      ],
      "callPatterns": [
        "New matter inquiry (approved party details for staff conflict review; consultation subject to confirmation)",
        "Existing client routing (verify matter, route to the assigned attorney or paralegal during business hours)",
        "Urgent matter flagging (caller-reported deadline or court date, flagged for immediate attorney review)",
        "Consultation scheduling (free or paid consult, requested on the connected calendar)",
        "General inquiry (fee structure or practice area questions routed to a callback, never legal advice on the call)"
      ]
    },
    "stats": [
      {
        "num": "configured after-hours coverage",
        "label": "after-hours and weekend calls can be answered instead of hitting voicemail"
      }
    ],
    "objections": [
      {
        "q": "Will it sound like a robot to my callers?",
        "a": "Use a trial call with your business wording to judge the voice and responses. Test a human handoff separately, including what happens when nobody answers and what caller details reach the recipient."
      },
      {
        "q": "Can it actually run a conflict check?",
        "a": "No, the AI does not run the conflict check itself. It collects the caller's name and the names of any other parties involved on the call, then routes that information to your staff so a real conflict check can be run before the consultation is confirmed."
      },
      {
        "q": "Will it give legal advice by mistake?",
        "a": "Configure the flow to collect approved intake information and request a consultation subject to firm confirmation. Questions seeking legal advice should go to firm staff. Test that boundary with example calls before launch."
      },
      {
        "q": "What about Spanish-speaking callers?",
        "a": "We're US English first today. Bilingual handoff is on the roadmap. If Spanish is critical to your market, talk to us about timeline before signing."
      }
    ],
    "callFaqs": [
      {
        "q": "What happens if a caller says they have a filing deadline coming up?",
        "a": "The AI flags the call as urgent and can send a configured summary to your team, including any dates the caller gives, for attorney review. The AI does not tell the caller whether they are inside or outside a deadline."
      },
      {
        "q": "Can it tell an existing client from a new caller?",
        "a": "If an approved connection can match the caller to an existing matter, the request can follow the configured matter-contact rule. Otherwise, collect the request for staff verification and callback. A stated matter number alone is not a verified identity match."
      },
      {
        "q": "Does it collect the information needed for a conflict check?",
        "a": "Yes. It asks for the caller's name and the names of any other parties or entities involved, then sends that to your staff so a conflict check can be run before the consultation is confirmed."
      },
      {
        "q": "Is this a per-call answering service or a flat monthly subscription?",
        "a": "Chleb AI is a flat monthly subscription: CRM at $297 a month, AI Receptionist + Chatbot at $899 a month, or the Front Office Bundle at $997 a month. Call minutes and emails are billed separately at carrier cost, not per call."
      },
      {
        "q": "What happens if a caller asks a general legal question instead of booking a consult?",
        "a": "The AI does not answer legal questions on the call. It takes down the question and routes it to your office as a callback during business hours."
      },
      {
        "q": "Does the AI quote fees or retainer terms on the phone?",
        "a": "No. Fee structure and retainer terms are confirmed with the caller by your staff, not quoted by the AI on the call."
      }
    ],
    "whyChleb": [
      "Configured voice, follow-up and CRM workflow in one bundle",
      "Vertical-tuned out of the box, not configured generically",
      "Keeps your existing business number",
      "Missed-call follow-up can be configured and tested",
      "One monthly subscription; usage is billed separately at carrier cost"
    ],
    "relatedComparisons": [
      {
        "competitor": "Smith.ai",
        "slug": "smith-ai",
        "note": "Compare the current Smith.ai offer with a configured law-firm call flow"
      },
      {
        "competitor": "Goodcall",
        "slug": "goodcall",
        "note": "Compare the current Goodcall offer with a configured call flow"
      }
    ]
  },
];

export const findVertical = (slug: string) => VERTICALS.find((v) => v.slug === slug);
